import React, { useEffect, useState } from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import useResendCooldown from '../../hooks/useResendCooldown';
import { BRAND } from '../../theme/brandTokens';

const RESEND_COOLDOWN_SECONDS = 60;

const LinkSentConfirmation = ({ message, email, onResend, onBackToLogin }) => {
  const { t } = useTranslation();
  const { secondsLeft, isActive, start } = useResendCooldown(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (isActive || resending) return;

    setResendError('');
    setResending(true);
    try {
      await onResend();
      start();
    } catch {
      setResendError('common.resendError');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={{ width: '100%', px: 1, pb: { xs: 1, sm: 0 }, textAlign: 'center' }}>
      <Alert severity="success" sx={{ width: '100%', mb: email ? 1 : 3, textAlign: 'left' }}>
        {message}
      </Alert>

      {email && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, wordBreak: 'break-word' }}>
          {email}
        </Typography>
      )}

      {resendError && (
        <Alert severity="error" sx={{ width: '100%', mb: 2, textAlign: 'left' }}>
          {t(resendError)}
        </Alert>
      )}

      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={handleResend}
        disabled={isActive || resending}
        sx={{
          color: BRAND.accentText,
          textDecoration: 'none',
          fontSize: '0.9rem',
          fontWeight: 600,
          '&:hover': {
            textDecoration: 'underline',
          },
          '&:disabled': {
            color: BRAND.textSubtle,
            cursor: 'not-allowed',
          },
        }}
      >
        {resending
          ? t('common.sending')
          : isActive
            ? t('common.resendIn', { seconds: secondsLeft })
            : t('common.resend')}
      </Link>

      <PrimaryActionButton
        fullWidth
        onClick={onBackToLogin}
        sx={{
          mt: 3,
          py: 1.5,
          fontSize: '1.1rem',
        }}
      >
        {t('common.backToLogin')}
      </PrimaryActionButton>
    </Box>
  );
};

export default LinkSentConfirmation;
